import type { RouteComponentProps } from '@reach/router';
import type { FieldProps, FormikHelpers } from 'formik';
import { Field, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';
import * as Yup from 'yup';

import { PasswordRules } from '@components/PasswordRules';
import RoundedButton from '@components/RoundedButton/index';
import TextInputBox from '@components/TextInputBox';

import * as ROUTES from '@constants/routes';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useSession } from '@hooks/useSession';

import AuthLayout, {
  BackLink,
  HorizontalRule,
  Subtitle,
} from '@layouts/AuthLayout';

const messages = defineMessages({
  header: {
    id: 'completeNewPassword.emailForm.header',
    defaultMessage: 'New Password Required',
  },
  subheader: {
    id: 'completeNewPassword.emailForm.asdf',
    defaultMessage: 'Please enter a new password to continue.',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  password: {
    id: 'completeNewPassword.emailForm.password',
    defaultMessage: 'New Password',
  },
  confirmPassword: {
    id: 'completeNewPassword.emailForm.confirmPassword',
    defaultMessage: 'Confirm New Password',
  },
  notFound: {
    id: 'completeNewPassword.emailForm.notFound',
    defaultMessage: 'That email does not exist in our system.',
  },
  exampleEmail: {
    id: 'completeNewPassword.emailForm.exampleEmail',
    defaultMessage: 'example@email.com',
  },
  goBack: {
    id: 'completeNewPassword.emailForm.goBack',
    defaultMessage: 'Back to login',
  },
  buttonText: {
    id: 'completeNewPassword.emailForm.buttonText',
    defaultMessage: 'SUBMIT',
  },
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  sendingError: {
    id: 'completeNewPassword.emailForm.sendingError',
    defaultMessage: 'There was an error with setting your password',
  },
  awaitingVerification: {
    id: 'completeNewPassword.emailForm.awaitingVerification',
    defaultMessage: 'Could not verify reCaptcha',
  },
  success: {
    id: 'completeNewPassword.emailForm.success',
    defaultMessage: 'Password changed!',
  },
  passwordsMustMatch: {
    id: 'passwordRules.passwordsMustMatch',
    defaultMessage: 'Passwords must match',
  },
});

type EmailFormValues = {
  newPassword: string;
  confirmNewPassword: string;
};

const EmailForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { respondToAuthChallenge } = useSession();
  const { passwordSchema } = useCommonSchemas();
  const email = useSearchParam('email');

  React.useEffect(() => {
    if (!email) {
      navigate(ROUTES.LOGIN);
    }
  }, [email]);

  // Validation schema
  const ResetFormSchema = Yup.object().shape({
    newPassword: passwordSchema.required(formatMessage(messages.requiredText)),
    confirmNewPassword: Yup.string()
      .oneOf(
        [Yup.ref('newPassword')],
        formatMessage(messages.passwordsMustMatch)
      )
      .required(formatMessage(messages.requiredText)),
  });

  async function handleSubmit(
    { newPassword }: EmailFormValues,
    { setErrors }: FormikHelpers<EmailFormValues>
  ) {
    if (!email) {
      return;
    }

    try {
      await respondToAuthChallenge('NEW_PASSWORD_REQUIRED', {
        NEW_PASSWORD: newPassword,
        USERNAME: email,
      });
    } catch {
      setErrors({ newPassword: formatMessage(messages.sendingError) });
    }
  }

  return (
    <AuthLayout title={formatMessage(messages.header)}>
      <Subtitle>{formatMessage(messages.subheader)}</Subtitle>

      <Formik
        enableReinitialize
        initialValues={{
          newPassword: '',
          confirmNewPassword: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={ResetFormSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <Field name="newPassword">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.password)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder="••••••••••"
                  required
                  type="password"
                  {...field}
                />
              )}
            </Field>
            <Field name="confirmNewPassword">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.confirmPassword)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder="••••••••••"
                  required
                  type="password"
                  {...field}
                />
              )}
            </Field>

            <PasswordRules />

            <HorizontalRule />

            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.buttonText)}
            </RoundedButton>
          </Form>
        )}
      </Formik>
      <BackLink to={ROUTES.LOGIN}>{formatMessage(messages.goBack)}</BackLink>
    </AuthLayout>
  );
};

export default EmailForm;
